import { Suspense, useContext, useEffect, useState } from "react";
import { Label, Modal } from "flowbite-react";
import { AuthContext } from "../../../../../common/components/auth/authContext/AuthContext";
import Loading from "../../../../../common/components/ui/loading/Loading";
import { approvePaymentCollector, cancelPaymentCollector, getReportRepossession } from "../../../../../common/services/repossession.api";
import { getCompanyId } from "../../../../../common/services/company.api";
import { REPOSSESSION_STATUS } from "../../../../../types/repossession.type";
import { FaMoneyBill, FaTrash } from "react-icons/fa";
import DatePicker from "react-datepicker";
import { HiMiniCalendarDays } from "react-icons/hi2";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import { useNavigate } from "react-router-dom";
import './override-modal-close.css';
import { getRenterCollectors } from "@/common/services/renters.api";

type ReportRepossession = {
  totalByCity: Record<string, { total: number }>;
  totalByStatus: Record<string, { total: number }>;
  totalByCollector: Record<string, { total: number }>;
  pendingPayments: Record<string, { plate: string; date: string }[]>;
};

const STATUS_LABELS = {
  [REPOSSESSION_STATUS.PENDING]: "Pendente",
  [REPOSSESSION_STATUS.COMPLETED]: "Recolhido",
  [REPOSSESSION_STATUS.SUSPENDED]: "Suspenso",
  [REPOSSESSION_STATUS.CANCELED]: "Pago/Negociado",
};

function getStatusLabels(status: string | REPOSSESSION_STATUS) {
  return STATUS_LABELS[status as REPOSSESSION_STATUS] || status;
}

function TableRepossession() {
  const navigate = useNavigate();
  useContext(AuthContext);
  const companyId = getCompanyId();

  const [repossessions, setRepossessions] = useState<ReportRepossession | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const today = new Date();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([today, today]);
  const [startDate, endDate] = dateRange;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [collectors, setCollectors] = useState<any[]>([]);
  const [, setLoadingCollectors] = useState(false);
  const { notifySuccess, notifyError } = useToastContext();

  const [selectedCollector, setSelectedCollector] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const openCollectorModal = (collector: string) => {
    setSelectedCollector(collector);
    setShowModal(true);
  };

  const closeCollectorModal = () => {
    setSelectedCollector(null);
    setShowModal(false);
  };


  useEffect(() => {
    async function loadRepossessions() {
      if (!startDate || !endDate) return;
      setIsLoading(true);
      try {
        const response = await getReportRepossession(companyId, startDate, endDate);
        setRepossessions(response.data);
      } catch (error) {
        console.error("Erro ao carregar relatório:", error);
      } finally {
        setIsLoading(false);
      }
    }

    loadRepossessions();
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    async function fetchCollectors() {
      try {
        setLoadingCollectors(true);
        const response = await getRenterCollectors();
        if (response?.data) {
          setCollectors(response.data);
        }
      } catch (error) {
        console.error("Erro ao buscar coletores:", error);
      } finally {
        setLoadingCollectors(false);
      }
    }
    fetchCollectors();
  }, [companyId]);

  const handlePaymentApprove = (collector: string, index: number) => async () => {
    if (!repossessions) return;
    const vehicle = repossessions.pendingPayments[collector]?.[index];
    if (!vehicle) {
      notifyError("Veículo não encontrado.");
      return;
    }

    const licensePlate = vehicle.plate;

    try {
      await approvePaymentCollector(licensePlate, collector);
      notifySuccess(`Pagamento aprovado com sucesso!`);
      setRepossessions((prev) => {
        if (!prev) return prev;
        const updatedPendingPayments = { ...prev.pendingPayments };
        updatedPendingPayments[collector] = updatedPendingPayments[collector].filter((_, idx) => idx !== index);
        return {
          ...prev,
          pendingPayments: updatedPendingPayments,
        };
      });
    } catch (error) {
      console.error("Erro ao aprovar pagamento:", error);
      notifyError(`Erro ao aprovar pagamento`);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("pt-BR");
  const handlePaymentRemove = (collector: string, index: number) => async () => {
    if (!repossessions) return;
    const vehicle = repossessions.pendingPayments[collector]?.[index];
    if (!vehicle) {
      notifyError("Veículo não encontrado.");
      return;
    }

    const licensePlate = vehicle.plate;
    try {
      await cancelPaymentCollector(licensePlate, collector);
      notifySuccess(`Pagamento removido com sucesso!`);
      setRepossessions((prev) => {
        if (!prev) return prev;
        const updatedPendingPayments = { ...prev.pendingPayments };
        updatedPendingPayments[collector] = updatedPendingPayments[collector].filter((_, idx) => idx !== index);
        return {
          ...prev,
          pendingPayments: updatedPendingPayments,
        };
      });
    } catch (error) {
      console.error("Erro ao remover pagamento:", error);
      notifyError(`Erro ao remover pagamento`);
    }
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="bg-gray-100 mx-auto min-h-screen py-10 px-4">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div>
                <label className="font-semibold text-[30px]">Relatório de Recolhimentos</label>
              </div>
              <p>Veja seus dados de recolhimentos e ações pendentes</p>
            </div>


            <div className="datepicker-style-div">
              <Label value="Data:" className="label" />
              <DatePicker
                locale="pt-Br"
                selectsRange
                maxDate={new Date()}
                startDate={startDate}
                endDate={endDate}
                onChange={(update: [Date | null, Date | null]) => setDateRange(update)}
                className="date-picker"
                placeholderText="Data Inicial e Final"
                dateFormat="dd/MM/yyyy"
                onFocus={(e) => (e.target as HTMLInputElement).readOnly = true}
                icon={<HiMiniCalendarDays />}
                showIcon={!endDate}
                toggleCalendarOnIconClick={true}
                swapRange
              />
            </div>
          </div>

          {isLoading || !repossessions ? (
            <Loading />
          ) : (
            <>
              <div
                className="border border-zinc-300 p-6 rounded-2xl shadow-lg flex justify-between items-center h-32 transition hover:scale-[1.02] hover:border-zinc-400 cursor-pointer"
                onClick={() => navigate("/relatorios/recolhimento/recolhedores")}
              >
                <span className="text-black text-lg font-medium">Recolhedores</span>
                <span className="text-4xl font-semibold text-locavibe">{collectors.length}</span>
              </div>
              <section>
                <h3 className="text-xl font-bold text-black mb-4">Resumo por Cidade</h3>
                {Object.entries(repossessions.totalByCity).length === 0 ? (
                  <p className="text-black text-center">Nenhum dado encontrado.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {Object.entries(repossessions.totalByCity)
                      .sort(([, a], [, b]) => b.total - a.total) // 🔥 Ordena do maior para o menor
                      .map(([city, info]) => (
                        <div
                          key={city}
                          className="border border-zinc-300 p-6 rounded-2xl shadow-lg flex justify-between items-center h-32 transition hover:scale-[1.02] hover:border-zinc-400"
                        >
                          <span className="text-black text-lg font-medium">{city}</span>
                          <span className="text-4xl font-semibold text-locavibe">{info.total}</span>
                        </div>
                      ))}
                  </div>
                )}
              </section>



              {/* Resumo por Status */}
              <section>
                <h3 className="text-xl font-bold text-black mb-4">Resumo por Status</h3>
                {Object.entries(repossessions.totalByStatus).length === 0 ? (
                  <p className="text-gray-500 text-center">Nenhum status encontrado.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {Object.entries(repossessions.totalByStatus).map(([status, info]) => (
                      <div
                        key={status}
                        className=" border border-zinc-300 p-6 rounded-2xl shadow-lg flex justify-between items-center h-32 transition hover:scale-[1.02] hover:border-zinc-400"
                      >
                        <span className="text-lg text-gray-600 font-medium">{getStatusLabels(status)}</span>
                        <span className="text-3xl font-semibold text-locavibe">{info.total}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Resumo por Recolhedor */}
              {/* <section>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Resumo por Recolhedor</h3>
                {Object.entries(repossessions.totalByCollector).length === 0 ? (
                  <p className="text-gray-500 text-center">Nenhum recolhedor encontrado.</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(repossessions.totalByCollector).map(([collector, info]) => (
                      <div
                        key={collector}
                        className="bg-white p-4 rounded-lg shadow flex justify-between items-center"
                      >
                        <span className="text-gray-700 font-medium">{collector}</span>
                        <span className="text-xl font-semibold text-purple-600">{info.total}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section> */}

              <section>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Ações Pendentes</h3>
                {Object.keys(repossessions.pendingPayments).length === 0 ? (
                  <p className="text-gray-500 text-center">Nenhuma ação pendente.</p>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(repossessions.pendingPayments).map(([collector, vehicles]) => (
                      <div key={collector}
                        className=" border border-zinc-300 p-6 rounded-2xl shadow-lg flex justify-between items-center transition hover:scale-[1.02] hover:border-zinc-400"
                      >
                        <button
                          onClick={() => openCollectorModal(collector)}
                          className="w-full text-left text-lg font-semibold text-blue-700 hover:underline flex justify-between items-center"
                        >
                          {collector}
                          <span className="text-locavibe">
                            ({vehicles.length} ação pendentes)
                          </span>
                        </button>
                      </div>
                    ))}

                    {showModal && selectedCollector && (
                      <Modal show={showModal} onClose={closeCollectorModal} size="xl" className="[&>div]:text-black">
                        <Modal.Header className="!text-black hover:text-black" style={{ color: 'black' }}>
                          <div className="text-black font-bold text-lg">
                            Ações Pendentes - {selectedCollector}
                          </div>
                        </Modal.Header>

                        <Modal.Body>
                          {repossessions?.pendingPayments[selectedCollector]?.length ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full bg-white text-black border border-gray-200 text-sm">
                                <thead>
                                  <tr className="bg-gray-100 text-gray-700">
                                    <th className="px-4 py-2 text-left">Data</th>
                                    <th className="px-4 py-2 text-left">Placa</th>
                                    <th className="px-4 py-2 text-left">Ações</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {repossessions.pendingPayments[selectedCollector].map((info, idx) => (
                                    <tr key={`${selectedCollector}-${idx}`} className="border-t ">
                                      <td className="px-4 py-2">{formatDate(info.date)}</td>
                                      <td className="px-4 py-2">{info.plate}</td>
                                      <td className="px-4 py-2">
                                        <div className="flex gap-4 text-lg">
                                          <button
                                            onClick={handlePaymentApprove(selectedCollector, idx)}
                                            title="Registrar pagamento"
                                            className="text-greenPrimary hover:text-green-800 transition"
                                          >
                                            <FaMoneyBill />
                                          </button>
                                          <button
                                            onClick={handlePaymentRemove(selectedCollector, idx)}
                                            title="Excluir vínculo"
                                            className="text-redPrimary hover:text-red-800 transition"
                                          >
                                            <FaTrash />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center">Nenhuma ação encontrada.</p>
                          )}
                        </Modal.Body>
                      </Modal>
                    )}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>

    </Suspense>
  );
}

export default TableRepossession;
